import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { GpsAccuracyBandPicker } from "../components/GpsAccuracyBandPicker";
import { OfferHoldPicker } from "../components/OfferHoldPicker";
import { PingIntervalPicker } from "../components/PingIntervalPicker";
import { LiveCheckinMaxAgePicker } from "../components/LiveCheckinMaxAgePicker";
import { ReplayRetentionPicker } from "../components/ReplayRetentionPicker";
import { Switch } from "../components/Switch";

import { fetchDutyTrackingPolicy, STALE_INTERVAL_MULTIPLIER, type DutyTrackingPolicy } from "../data/duty-pings";
import {
  GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS,
  GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS,
  isAllowedGpsAccuracyHighMax,
  isAllowedGpsAccuracyMediumMax,
  isLegalGpsAccuracyBandChoice,
  type GpsAccuracyHighMaxMeters,
  type GpsAccuracyMediumMaxMeters,
} from "../data/gps-accuracy-bands";
import {
  upsertDutyCallOfferPolicy,
  upsertDutyTrackingPolicy,
  upsertGeofenceAlertPolicy,
  upsertGpsAccuracyBandPolicy,
  upsertLiveCheckinMaxAgePolicy,
  upsertReplayRetentionPolicy,
  type DutyCallOfferPolicyDraft,
  type DutyPolicyDraft,
  type GeofencePolicyDraft,
  type GpsAccuracyBandPolicyDraft,
  type LiveCheckinMaxAgePolicyDraft,
  type ReplayRetentionPolicyDraft,
} from "../data/operator-controls";
import { formatOfferHold, isOfferHoldSecondsInRange } from "../lib/offer-hold";
import { useReadModel } from "../data/useReadModel";

// Account-admin duty-tracking policy row (PRD-001J/J4). Reads the hosted
// public.get_account_duty_tracking_policy RPC (security-invoker; member-scoped) — the same single
// source of truth the mobile app consents against. Write controls only mount behind policy.manage
// and call a hosted RPC; the browser never writes policy tables directly. Fail closed behind
// policy.view.

const SCOPE_COPY: Record<string, string> = {
  on_duty_safety: "On-duty safety",
  foreground_scan_only: "Foreground scan only",
  none: "None",
};

function formatInterval(seconds: number): string {
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return minutes === 1 ? "1 min" : `${minutes} min`;
  }
  return `${seconds}s`;
}

export function DutyTrackingPolicyRow({
  client,
  accountId,
  canView = false,
  canManage = false,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  /** policy.view capability — fail closed. */
  canView?: boolean;
  /** policy.manage capability — write affordances only exist when true. */
  canManage?: boolean;
  pollMs?: number;
}) {
  if (!canView) {
    return null;
  }
  return (
    <DutyTrackingPolicyRowInner
      key={accountId}
      client={client}
      accountId={accountId}
      canManage={canManage}
      {...(pollMs !== undefined ? { pollMs } : {})}
    />
  );
}

interface PolicyDraftState<T> {
  draft: T;
  dirty: boolean;
}

function usePolicyDraft<T extends object>(
  policy: DutyTrackingPolicy,
  createDraft: (policy: DutyTrackingPolicy) => T,
): {
  draft: T;
  /** True once the operator changed something that has not been saved yet. */
  dirty: boolean;
  updateDraft: (patch: Partial<T>) => void;
  markSaved: (submittedDraft: T) => void;
} {
  const [state, setState] = useState<PolicyDraftState<T>>(() => ({
    draft: createDraft(policy),
    dirty: false,
  }));

  useEffect(() => {
    setState((current) =>
      current.dirty
        ? current
        : {
            draft: createDraft(policy),
            dirty: false,
          },
    );
  }, [policy, createDraft]);

  return {
    draft: state.draft,
    dirty: state.dirty,
    updateDraft: (patch) => {
      setState((current) => ({
        draft: { ...current.draft, ...patch },
        dirty: true,
      }));
    },
    markSaved: (submittedDraft) => {
      setState((current) =>
        current.draft === submittedDraft
          ? { ...current, dirty: false }
          : current,
      );
    },
  };
}

/** Split so the read only mounts behind the canView gate. */
function DutyTrackingPolicyRowInner({
  client,
  accountId,
  canManage,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  pollMs?: number;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<DutyTrackingPolicy>(
    () => fetchDutyTrackingPolicy(client, accountId),
    [client, accountId, reloadNonce],
    pollMs !== undefined ? { pollMs } : {},
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading duty tracking policy…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the duty tracking policy."}
      </p>
    );
  }

  const policy = data;
  if (policy === null) {
    return null;
  }

  const scopeLabel = SCOPE_COPY[policy.locationScope] ?? policy.locationScope;
  const staleAfter = formatInterval(policy.pingIntervalSeconds * STALE_INTERVAL_MULTIPLIER);

  return (
    <div className="duty-policy">
      <div className="duty-policy__head">
        <span
          className={`rollout__badge ${policy.enabled ? "rollout__badge--ready" : "rollout__badge--disabled"}`}
        >
          {policy.enabled ? "Enabled" : "Disabled"}
        </span>
        <span className="duty-policy__summary">
          {policy.enabled
            ? "Guards on duty report location at the interval below while clocked in."
            : "On-duty location accountability is off for this account."}
        </span>
      </div>
      <dl className="duty-policy__grid">
        <div className="duty-policy__cell">
          <dt>Ping interval</dt>
          <dd>{formatInterval(policy.pingIntervalSeconds)}</dd>
        </div>
        <div className="duty-policy__cell">
          <dt>Stale after</dt>
          <dd>{staleAfter} ({STALE_INTERVAL_MULTIPLIER}× interval)</dd>
        </div>
        <div className="duty-policy__cell">
          <dt>Requires clock-in</dt>
          <dd>{policy.requireClockIn ? "Yes" : "No"}</dd>
        </div>
        <div className="duty-policy__cell">
          <dt>Location scope</dt>
          <dd>{scopeLabel}</dd>
        </div>
        <div className="duty-policy__cell">
          <dt>Consent key</dt>
          <dd>{policy.consentKey}</dd>
        </div>
      </dl>
      <div className="duty-policy__subsection">
        <div className="duty-policy__head">
          <span className="rollout__badge rollout__badge--ready">Replay calendar</span>
          <span
            className="toggle-chip toggle-chip--on duty-policy__retention-chip"
            aria-label={`Replay retention ${policy.replayRetentionDays} days`}
          >
            {policy.replayRetentionDays} days
          </span>
          <span className="duty-policy__summary">
            Supervisors can replay recorded duty activity from the last {policy.replayRetentionDays} days.
            Older days are unavailable, not an empty tape.
          </span>
        </div>
        <dl className="duty-policy__grid">
          <div className="duty-policy__cell">
            <dt>Calendar length</dt>
            <dd>{policy.replayRetentionDays} days</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Older days</dt>
            <dd>Unavailable</dd>
          </div>
        </dl>
      </div>
      <div className="duty-policy__subsection">
        <div className="duty-policy__head">
          <span className="rollout__badge rollout__badge--ready">Live check-ins</span>
          <span
            className="toggle-chip toggle-chip--on duty-policy__retention-chip"
            aria-label={`Live check-ins ${policy.liveCheckinMaxAgeHours} hours`}
          >
            {policy.liveCheckinMaxAgeHours}h
          </span>
          <span className="duty-policy__summary">
            Command Live paints Recent check-in dots from the last {policy.liveCheckinMaxAgeHours}{" "}
            hours. Older captures stay in Activity and Replay.
          </span>
        </div>
        <dl className="duty-policy__grid">
          <div className="duty-policy__cell">
            <dt>Live window</dt>
            <dd>{policy.liveCheckinMaxAgeHours} hours</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Older check-ins</dt>
            <dd>Activity / Replay only</dd>
          </div>
        </dl>
      </div>
      <div className="duty-policy__subsection">
        <div className="duty-policy__head">
          <span className="rollout__badge rollout__badge--ready">GPS accuracy bands</span>
          <span
            className="toggle-chip toggle-chip--on duty-policy__retention-chip"
            aria-label={`GPS accuracy High up to ${policy.gpsAccuracyHighMaxMeters} metres, Medium up to ${policy.gpsAccuracyMediumMaxMeters} metres`}
          >
            {policy.gpsAccuracyHighMaxMeters} / {policy.gpsAccuracyMediumMaxMeters} m
          </span>
          <span className="duty-policy__summary">
            Timeclock and Command Live label a reported fix High up to{" "}
            {policy.gpsAccuracyHighMaxMeters} m, Medium up to {policy.gpsAccuracyMediumMaxMeters} m,
            and Low beyond that. Labels only: phone capture accuracy is unchanged.
          </span>
        </div>
        <dl className="duty-policy__grid">
          <div className="duty-policy__cell">
            <dt>High</dt>
            <dd>≤ {policy.gpsAccuracyHighMaxMeters} m</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Medium</dt>
            <dd>≤ {policy.gpsAccuracyMediumMaxMeters} m</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Low</dt>
            <dd>&gt; {policy.gpsAccuracyMediumMaxMeters} m</dd>
          </div>
        </dl>
      </div>
      <div className="duty-policy__subsection">
        <div className="duty-policy__head">
          <span
            className={`rollout__badge ${policy.geofenceAlertsEnabled ? "rollout__badge--ready" : "rollout__badge--disabled"}`}
          >
            {policy.geofenceAlertsEnabled ? "Geofence on" : "Geofence off"}
          </span>
          <span className="duty-policy__summary">
            {policy.geofenceAlertsEnabled
              ? "Left-site alerts fire when a clocked-in guard is outside the L0 site boundary."
              : "Site boundary exit alerts are off for this account."}
          </span>
        </div>
        <dl className="duty-policy__grid">
          <div className="duty-policy__cell">
            <dt>Min GPS accuracy</dt>
            <dd>{policy.geofenceMinAccuracyMeters} m</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Exit buffer</dt>
            <dd>{policy.geofenceExitBufferMeters} m</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Exit confirm pings</dt>
            <dd>{policy.geofenceExitConfirmPings}</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Re-entry confirm pings</dt>
            <dd>{policy.geofenceEnterConfirmPings}</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Alert cooldown</dt>
            <dd>{formatInterval(policy.geofenceCooldownSeconds)}</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Supervisor push</dt>
            <dd>{policy.geofenceSupervisorPushEnabled ? "On" : "Off"}</dd>
          </div>
        </dl>
      </div>
      <div className="duty-policy__subsection">
        <div className="duty-policy__head">
          <span
            className={`rollout__badge ${policy.unansweredOfferHoldEnabled ? "rollout__badge--ready" : "rollout__badge--disabled"}`}
          >
            {policy.unansweredOfferHoldEnabled ? "Offer hold on" : "Offer hold off"}
          </span>
          <span className="duty-policy__summary">
            {policy.unansweredOfferHoldEnabled
              ? "An unanswered directed Call offer closes after the hold below and the Call stays open for Dispatch."
              : "Unanswered directed Call offers stay open until the officer answers or Dispatch cancels them."}
          </span>
        </div>
        <dl className="duty-policy__grid">
          <div className="duty-policy__cell">
            <dt>Hold length</dt>
            <dd>{formatOfferHold(policy.unansweredOfferHoldSeconds)}</dd>
          </div>
          <div className="duty-policy__cell">
            <dt>Officer countdown</dt>
            <dd>None. The officer only Accepts or Declines.</dd>
          </div>
        </dl>
      </div>
      {canManage ? (
        <>
          <DutyPolicyEditor
            client={client}
            accountId={accountId}
            policy={policy}
            onSaved={() => setReloadNonce((nonce) => nonce + 1)}
          />
          <GeofencePolicyEditor
            client={client}
            accountId={accountId}
            policy={policy}
            onSaved={() => setReloadNonce((nonce) => nonce + 1)}
          />
          <DutyCallOfferPolicyEditor
            client={client}
            accountId={accountId}
            policy={policy}
            onSaved={() => setReloadNonce((nonce) => nonce + 1)}
          />
          <ReplayRetentionPolicyEditor
            client={client}
            accountId={accountId}
            policy={policy}
            onSaved={() => setReloadNonce((nonce) => nonce + 1)}
          />
          <LiveCheckinMaxAgePolicyEditor
            client={client}
            accountId={accountId}
            policy={policy}
            onSaved={() => setReloadNonce((nonce) => nonce + 1)}
          />
          <GpsAccuracyBandPolicyEditor
            client={client}
            accountId={accountId}
            policy={policy}
            onSaved={() => setReloadNonce((nonce) => nonce + 1)}
          />
        </>
      ) : (
        <p className="page__note">Read-only for this role.</p>
      )}
    </div>
  );
}

function DutyPolicyEditor({
  client,
  accountId,
  policy,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  policy: DutyTrackingPolicy;
  onSaved: () => void;
}) {
  const { draft, updateDraft: updatePolicyDraft, markSaved } = usePolicyDraft(policy, toDraft);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  function updateDraft(patch: Partial<DutyPolicyDraft>): void {
    updatePolicyDraft(patch);
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy) return;
    if (!Number.isInteger(draft.pingIntervalSeconds) || draft.pingIntervalSeconds < 60 || draft.pingIntervalSeconds > 3600) {
      setFailure("Duty ping interval must be between 60 and 3600 seconds.");
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const submittedDraft = draft;
    const outcome = await upsertDutyTrackingPolicy(client, accountId, submittedDraft);
    if (outcome.ok) {
      markSaved(submittedDraft);
      setReceipt("Duty policy saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form className="duty-policy__editor" aria-label="Edit duty tracking policy" onSubmit={(event) => event.preventDefault()}>
      <div className="so-switch-stack">
        <Switch
          checked={draft.enabled}
          onChange={(enabled) => updateDraft({ enabled })}
          label="Duty tracking enabled"
          hint="When on, clocked-in guards send location pings at the interval below."
        />
        <Switch
          checked={draft.requireClockIn}
          onChange={(requireClockIn) => updateDraft({ requireClockIn })}
          label="Requires clock-in"
          hint="Pings only count after the guard clocks in on mobile."
        />
      </div>
      <PingIntervalPicker
        pingIntervalSeconds={draft.pingIntervalSeconds}
        onChange={(pingIntervalSeconds) => updateDraft({ pingIntervalSeconds })}
        disabled={!draft.enabled}
      />
      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Saving..." : "Save duty policy"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

function toDraft(policy: DutyTrackingPolicy): DutyPolicyDraft {
  return {
    enabled: policy.enabled,
    pingIntervalSeconds: policy.pingIntervalSeconds,
    requireClockIn: policy.requireClockIn,
  };
}

function toOfferPolicyDraft(policy: DutyTrackingPolicy): DutyCallOfferPolicyDraft {
  return {
    unansweredOfferHoldEnabled: policy.unansweredOfferHoldEnabled,
    unansweredOfferHoldSeconds: policy.unansweredOfferHoldSeconds,
  };
}

function toReplayRetentionDraft(policy: DutyTrackingPolicy): ReplayRetentionPolicyDraft {
  return { replayRetentionDays: policy.replayRetentionDays };
}

function toLiveCheckinMaxAgeDraft(policy: DutyTrackingPolicy): LiveCheckinMaxAgePolicyDraft {
  return { liveCheckinMaxAgeHours: policy.liveCheckinMaxAgeHours };
}

/**
 * A stored pair is always coherent (table CHECK), but it need not sit on the v1 chips: an older
 * or hand-applied row could hold, say, High 20. The chips only know 10 / 15 / 25 / 50, so the
 * draft snaps an off-chip value to the shipped default rather than inventing a chip, and the
 * read-only summary above keeps printing the real stored number.
 */
function toGpsAccuracyBandDraft(policy: DutyTrackingPolicy): GpsAccuracyBandPolicyDraft {
  const high: GpsAccuracyHighMaxMeters = isAllowedGpsAccuracyHighMax(policy.gpsAccuracyHighMaxMeters)
    ? policy.gpsAccuracyHighMaxMeters
    : GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS[2];
  const medium: GpsAccuracyMediumMaxMeters = isAllowedGpsAccuracyMediumMax(policy.gpsAccuracyMediumMaxMeters)
    ? policy.gpsAccuracyMediumMaxMeters
    : GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS[2];
  return { gpsAccuracyHighMaxMeters: high, gpsAccuracyMediumMaxMeters: medium };
}

/**
 * GPS accuracy band ceilings. Own writer, own Save, same F-2 dirty-draft guard as the other
 * editors. The save gate mirrors the host's three refusals (chip allowlists and Medium > High)
 * so an illegal pair is stopped here with a sentence, not bounced as an RPC error.
 */
function GpsAccuracyBandPolicyEditor({
  client,
  accountId,
  policy,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  policy: DutyTrackingPolicy;
  onSaved: () => void;
}) {
  const { draft, dirty, updateDraft, markSaved } = usePolicyDraft(policy, toGpsAccuracyBandDraft);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  // A stored ceiling that is not one of the chips (hand-applied row) is still what Timeclock
  // grades with. Say so, because Save would replace it with the chip the draft snapped to.
  const storedOffChip =
    !isAllowedGpsAccuracyHighMax(policy.gpsAccuracyHighMaxMeters)
    || !isAllowedGpsAccuracyMediumMax(policy.gpsAccuracyMediumMaxMeters);

  async function submit(): Promise<void> {
    if (busy || !dirty) return;
    if (!isLegalGpsAccuracyBandChoice(draft.gpsAccuracyHighMaxMeters, draft.gpsAccuracyMediumMaxMeters)) {
      setFailure(
        `Medium ceiling must be above the ${draft.gpsAccuracyHighMaxMeters} m High ceiling.`,
      );
      return;
    }
    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const submittedDraft = draft;
    const outcome = await upsertGpsAccuracyBandPolicy(client, accountId, submittedDraft);
    if (outcome.ok) {
      markSaved(submittedDraft);
      setReceipt("GPS accuracy bands saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form
      className="duty-policy__editor duty-policy__editor--gps-accuracy-bands"
      aria-label="Edit GPS accuracy bands"
      onSubmit={(event) => event.preventDefault()}
    >
      <GpsAccuracyBandPicker
        gpsAccuracyHighMaxMeters={draft.gpsAccuracyHighMaxMeters}
        gpsAccuracyMediumMaxMeters={draft.gpsAccuracyMediumMaxMeters}
        onChange={(next) => {
          updateDraft(next);
          setFailure(null);
          setReceipt(null);
        }}
      />
      {storedOffChip ? (
        <p className="setup__field-hint" role="note">
          Stored ceilings are {policy.gpsAccuracyHighMaxMeters} / {policy.gpsAccuracyMediumMaxMeters} m,
          which are not chip values. Saving replaces them with the chips above.
        </p>
      ) : null}
      <div className="policy__editor-actions">
        <button
          type="button"
          className="minter__confirm-go"
          disabled={busy || !dirty}
          onClick={() => void submit()}
        >
          {busy ? "Saving..." : "Save GPS accuracy bands"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

function ReplayRetentionPolicyEditor({
  client,
  accountId,
  policy,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  policy: DutyTrackingPolicy;
  onSaved: () => void;
}) {
  const { draft, updateDraft, markSaved } = usePolicyDraft(policy, toReplayRetentionDraft);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const submittedDraft = draft;
    const outcome = await upsertReplayRetentionPolicy(client, accountId, submittedDraft);
    if (outcome.ok) {
      markSaved(submittedDraft);
      setReceipt("Replay calendar saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form
      className="duty-policy__editor duty-policy__editor--replay-retention"
      aria-label="Edit Replay calendar"
      onSubmit={(event) => event.preventDefault()}
    >
      <ReplayRetentionPicker
        replayRetentionDays={draft.replayRetentionDays}
        onChange={(replayRetentionDays) => {
          updateDraft({ replayRetentionDays });
          setFailure(null);
          setReceipt(null);
        }}
      />
      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Saving..." : "Save Replay calendar"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

function LiveCheckinMaxAgePolicyEditor({
  client,
  accountId,
  policy,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  policy: DutyTrackingPolicy;
  onSaved: () => void;
}) {
  const { draft, updateDraft, markSaved } = usePolicyDraft(policy, toLiveCheckinMaxAgeDraft);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const submittedDraft = draft;
    const outcome = await upsertLiveCheckinMaxAgePolicy(client, accountId, submittedDraft);
    if (outcome.ok) {
      markSaved(submittedDraft);
      setReceipt("Live check-ins window saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form
      className="duty-policy__editor duty-policy__editor--live-checkin-max-age"
      aria-label="Edit Live check-ins window"
      onSubmit={(event) => event.preventDefault()}
    >
      <LiveCheckinMaxAgePicker
        liveCheckinMaxAgeHours={draft.liveCheckinMaxAgeHours}
        onChange={(liveCheckinMaxAgeHours) => {
          updateDraft({ liveCheckinMaxAgeHours });
          setFailure(null);
          setReceipt(null);
        }}
      />
      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Saving..." : "Save Live check-ins window"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

/**
 * Unanswered Call offers. Its own editor because the hold is its own hosted writer: the duty and
 * geofence saves carry the hold columns forward, and this save carries theirs, so no sibling row
 * can wipe another's value.
 *
 * `usePolicyDraft` is the F-2 dirty-draft guard: this row polls, and a poll landing mid-edit must
 * not reset the chips the operator just pressed.
 */
function DutyCallOfferPolicyEditor({
  client,
  accountId,
  policy,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  policy: DutyTrackingPolicy;
  onSaved: () => void;
}) {
  const { draft, updateDraft: updateOfferDraft, markSaved } = usePolicyDraft(
    policy,
    toOfferPolicyDraft,
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  function updateDraft(patch: Partial<DutyCallOfferPolicyDraft>): void {
    updateOfferDraft(patch);
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy) return;
    if (!isOfferHoldSecondsInRange(draft.unansweredOfferHoldSeconds)) {
      setFailure("Unanswered offer hold must be between 60 and 3600 seconds.");
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const submittedDraft = draft;
    const outcome = await upsertDutyCallOfferPolicy(client, accountId, submittedDraft);
    if (outcome.ok) {
      markSaved(submittedDraft);
      setReceipt("Unanswered Call offer policy saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form
      className="duty-policy__editor duty-policy__editor--offer-hold"
      aria-label="Edit unanswered Call offers"
      onSubmit={(event) => event.preventDefault()}
    >
      <Switch
        checked={draft.unansweredOfferHoldEnabled}
        onChange={(unansweredOfferHoldEnabled) => updateDraft({ unansweredOfferHoldEnabled })}
        label="Unanswered Call offers expire"
        hint="Independent of duty tracking: turning location pings off leaves this on, and the reverse."
      />
      <OfferHoldPicker
        unansweredOfferHoldSeconds={draft.unansweredOfferHoldSeconds}
        onChange={(unansweredOfferHoldSeconds) => updateDraft({ unansweredOfferHoldSeconds })}
        disabled={!draft.unansweredOfferHoldEnabled}
      />
      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Saving..." : "Save offer policy"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

function toGeofenceDraft(policy: DutyTrackingPolicy): GeofencePolicyDraft {
  return {
    geofenceAlertsEnabled: policy.geofenceAlertsEnabled,
    geofenceSupervisorPushEnabled: policy.geofenceSupervisorPushEnabled,
    geofenceMinAccuracyMeters: policy.geofenceMinAccuracyMeters,
    geofenceExitBufferMeters: policy.geofenceExitBufferMeters,
    geofenceExitConfirmPings: policy.geofenceExitConfirmPings,
    geofenceEnterConfirmPings: policy.geofenceEnterConfirmPings,
    geofenceCooldownSeconds: policy.geofenceCooldownSeconds,
  };
}

function GeofencePolicyEditor({
  client,
  accountId,
  policy,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  policy: DutyTrackingPolicy;
  onSaved: () => void;
}) {
  const { draft, updateDraft: updateGeofenceDraft, markSaved } = usePolicyDraft(
    policy,
    toGeofenceDraft,
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  function updateDraft(patch: Partial<GeofencePolicyDraft>): void {
    updateGeofenceDraft(patch);
    setFailure(null);
    setReceipt(null);
  }

  function parseDraft(): GeofencePolicyDraft | null {
    if (draft.geofenceMinAccuracyMeters <= 0) {
      setFailure("Minimum GPS accuracy must be greater than zero.");
      return null;
    }
    if (draft.geofenceExitBufferMeters < 0) {
      setFailure("Exit buffer must be zero or greater.");
      return null;
    }
    if (draft.geofenceExitConfirmPings < 1 || draft.geofenceExitConfirmPings > 10) {
      setFailure("Exit confirm pings must be between 1 and 10.");
      return null;
    }
    if (draft.geofenceEnterConfirmPings < 1 || draft.geofenceEnterConfirmPings > 10) {
      setFailure("Re-entry confirm pings must be between 1 and 10.");
      return null;
    }
    if (draft.geofenceCooldownSeconds < 0) {
      setFailure("Alert cooldown must be zero or greater.");
      return null;
    }
    return draft;
  }

  async function submit(): Promise<void> {
    if (busy) return;
    const parsed = parseDraft();
    if (parsed === null) return;

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const submittedDraft = parsed;
    const outcome = await upsertGeofenceAlertPolicy(client, accountId, submittedDraft);
    if (outcome.ok) {
      markSaved(submittedDraft);
      setReceipt("Geofence alert policy saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form
      className="duty-policy__editor duty-policy__editor--geofence"
      aria-label="Edit site boundary geofence alerts"
      onSubmit={(event) => event.preventDefault()}
    >
      <Switch
        checked={draft.geofenceAlertsEnabled}
        onChange={(geofenceAlertsEnabled) =>
          updateDraft({
            geofenceAlertsEnabled,
            geofenceSupervisorPushEnabled: geofenceAlertsEnabled,
          })
        }
        label="Site boundary exit alerts"
        hint="When on, consecutive duty pings outside the L0 site polygon can open a left-site alert."
      />
      <Switch
        checked={draft.geofenceSupervisorPushEnabled}
        onChange={(geofenceSupervisorPushEnabled) => updateDraft({ geofenceSupervisorPushEnabled })}
        disabled={!draft.geofenceAlertsEnabled}
        label="Supervisor Expo push"
        hint="When on, open geofence exit alerts also notify management on mobile push."
      />
      <div className="duty-policy__grid duty-policy__grid--editor">
        <label className="duty-policy__field">
          <span className="duty-policy__field-label">Min GPS accuracy (m)</span>
          <input
            type="number"
            min={1}
            step={1}
            value={draft.geofenceMinAccuracyMeters}
            disabled={!draft.geofenceAlertsEnabled}
            onChange={(event) =>
              updateDraft({ geofenceMinAccuracyMeters: Number(event.target.value) })
            }
          />
        </label>
        <label className="duty-policy__field">
          <span className="duty-policy__field-label">Exit buffer (m)</span>
          <input
            type="number"
            min={0}
            step={1}
            value={draft.geofenceExitBufferMeters}
            disabled={!draft.geofenceAlertsEnabled}
            onChange={(event) =>
              updateDraft({ geofenceExitBufferMeters: Number(event.target.value) })
            }
          />
        </label>
        <label className="duty-policy__field">
          <span className="duty-policy__field-label">Exit confirm pings</span>
          <input
            type="number"
            min={1}
            max={10}
            step={1}
            value={draft.geofenceExitConfirmPings}
            disabled={!draft.geofenceAlertsEnabled}
            onChange={(event) =>
              updateDraft({ geofenceExitConfirmPings: Number(event.target.value) })
            }
          />
        </label>
        <label className="duty-policy__field">
          <span className="duty-policy__field-label">Re-entry confirm pings</span>
          <input
            type="number"
            min={1}
            max={10}
            step={1}
            value={draft.geofenceEnterConfirmPings}
            disabled={!draft.geofenceAlertsEnabled}
            onChange={(event) =>
              updateDraft({ geofenceEnterConfirmPings: Number(event.target.value) })
            }
          />
        </label>
        <label className="duty-policy__field">
          <span className="duty-policy__field-label">Alert cooldown (s)</span>
          <input
            type="number"
            min={0}
            step={30}
            value={draft.geofenceCooldownSeconds}
            disabled={!draft.geofenceAlertsEnabled}
            onChange={(event) =>
              updateDraft({ geofenceCooldownSeconds: Number(event.target.value) })
            }
          />
        </label>
      </div>
      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Saving..." : "Save geofence policy"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}
